import * as React from 'react';

import { EStatus, TCheckContactsAccessParams, TContext, TEarlyAccessPaywallParams } from '../../types/context';
import { IEarlyAccessPaywall } from '../../types/earlyAccessPaywallModal';
import { TFetchManifestResult } from '../../types/microfrontend';
import { context as Context } from '../../utils/context';

import type { CianMFUIElement } from '@cian/mf-registry/browser/internal/cianMFUI';
import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

interface IProviderProps {
  fetchManifest: () => Promise<TFetchManifestResult>;
  params: TEarlyAccessPaywallParams;
}

export const CLOSE_MODAL = '/v1/close-modal';
export const CHECK_CONTACTS_ACCESS = '/v1/check-contacts-access';

export const Provider: React.FC<React.PropsWithChildren<IProviderProps>> = ({ children, fetchManifest, params }) => {
  const microfrontendRef = React.useRef<CianMFUIElement | null>(null);
  const [manifest, setManifest] = React.useState<IMicrofrontendManifest | null>(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const getLocalApi = React.useCallback(() => {
    const api = (microfrontendRef.current?.instance?.customElement as IEarlyAccessPaywall)?.api;
    if (!api) {
      throw new Error(
        'Не удалось получить доступ к локальному API микрофронтенда "early-access-paywall-microfrontend"',
      );
    }

    return api;
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const context = React.useMemo<TContext>(() => {
    const commonParameters = {
      microfrontendRef,
      params: {
        ...params,
      },
      open,
      onClose: handleClose,
      checkContactsAccess: async ({ offerId, limitType }: TCheckContactsAccessParams): Promise<boolean> => {
        if (!manifest) {
          setLoading(true);
          const result = await fetchManifest();
          setLoading(false);

          if (result) {
            setManifest(result.manifest);
          } else {
            setOpen(true);
            throw new Error('Не удалось загрузить манифест микрофронтенда "early-access-paywall-microfrontend"');
          }

          // Ждём пока микрофронт замаунтится
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        const isConfirmed = await getLocalApi()[CHECK_CONTACTS_ACCESS]({ offerId, limitType });
        setLoading(false);

        return isConfirmed;
      },
    };

    if (loading) {
      return {
        ...commonParameters,
        status: EStatus.Loading,
      };
    }

    if (!manifest) {
      return {
        ...commonParameters,
        status: EStatus.Failed,
      };
    }

    return {
      ...commonParameters,
      manifest,
      status: EStatus.Succeed,
    };
  }, [params, open, handleClose, loading, manifest, getLocalApi, fetchManifest]);

  return <Context.Provider value={context}>{children}</Context.Provider>;
};
